/**
 * Emits Mermaid `flowchart` text for the workspace's concepts + relations.
 *
 * Mermaid is intentionally less semantically rich than PlantUML — there
 * is no native "Composition diamond" or "Realization triangle" arrow in
 * flowchart syntax. We round-trip the relation type by appending a
 * structured tag to the edge label: `::<RelationType>`. The parser strips
 * the tag back off when reading.
 *
 * Node shapes are mapped per ArchiMate aspect:
 *   - behavior         → rounded:  `id("Label")`
 *   - active_structure → rectangle: `id["Label"]`
 *   - passive_structure→ rectangle: `id["Label"]`
 *   - motivation       → rhombus:  `id{"Label"}`
 *   - default          → rectangle
 *
 * Layer colors are emitted via Mermaid `classDef` and applied to each
 * node via the `:::className` suffix. The class name is the layer slug.
 */
import { legacyTypeIdFor } from "../archimate4Migration";
import type { BilingualText, Concept, Relation } from "../types";

/** Map ArchiMate type-IDs to a Mermaid shape + layer-color class. */
const TYPE_INFO: Record<string, { shape: NodeShape; layer: LayerSlug }> = {
  // Strategy
  "archimate.Resource": { shape: "rect", layer: "strategy" },
  "archimate.Capability": { shape: "round", layer: "strategy" },
  "archimate.ValueStream": { shape: "round", layer: "strategy" },
  "archimate.CourseOfAction": { shape: "round", layer: "strategy" },
  // Business
  "archimate.BusinessActor": { shape: "rect", layer: "business" },
  "archimate.BusinessRole": { shape: "rect", layer: "business" },
  "archimate.BusinessCollaboration": { shape: "rect", layer: "business" },
  "archimate.BusinessInterface": { shape: "rect", layer: "business" },
  "archimate.BusinessProcess": { shape: "round", layer: "business" },
  "archimate.BusinessFunction": { shape: "round", layer: "business" },
  "archimate.BusinessService": { shape: "round", layer: "business" },
  "archimate.BusinessObject": { shape: "rect", layer: "business" },
  "archimate.BusinessEvent": { shape: "round", layer: "business" },
  "archimate.Representation": { shape: "rect", layer: "business" },
  // Application
  "archimate.ApplicationComponent": { shape: "rect", layer: "application" },
  "archimate.ApplicationInterface": { shape: "rect", layer: "application" },
  "archimate.ApplicationFunction": { shape: "round", layer: "application" },
  "archimate.ApplicationProcess": { shape: "round", layer: "application" },
  "archimate.ApplicationService": { shape: "round", layer: "application" },
  "archimate.DataObject": { shape: "rect", layer: "application" },
  // Technology
  "archimate.Node": { shape: "rect", layer: "technology" },
  "archimate.Device": { shape: "rect", layer: "technology" },
  "archimate.SystemSoftware": { shape: "rect", layer: "technology" },
  "archimate.CommunicationNetwork": { shape: "rect", layer: "technology" },
  "archimate.TechnologyInterface": { shape: "rect", layer: "technology" },
  "archimate.TechnologyService": { shape: "round", layer: "technology" },
  "archimate.Artifact": { shape: "rect", layer: "technology" },
  // Implementation & Migration
  "archimate.Plateau": { shape: "rect", layer: "implementation" },
  "archimate.Gap": { shape: "rect", layer: "implementation" },
  "archimate.WorkPackage": { shape: "rect", layer: "implementation" },
  "archimate.Deliverable": { shape: "rect", layer: "implementation" },
  // Motivation
  "archimate.Stakeholder": { shape: "rhombus", layer: "motivation" },
  "archimate.Driver": { shape: "rhombus", layer: "motivation" },
  "archimate.Assessment": { shape: "rhombus", layer: "motivation" },
  "archimate.Goal": { shape: "rhombus", layer: "motivation" },
  "archimate.Outcome": { shape: "rhombus", layer: "motivation" },
  "archimate.Principle": { shape: "rhombus", layer: "motivation" },
  "archimate.Requirement": { shape: "rhombus", layer: "motivation" },
  "archimate.Constraint": { shape: "rhombus", layer: "motivation" },
  "archimate.Value": { shape: "rhombus", layer: "motivation" },
  // Composite
  "archimate.Grouping": { shape: "rect", layer: "composite" },
};

/**
 * Vorm + laagkleur voor een concept, via z'n 3.x-identiteit.
 *
 * `TYPE_INFO` is opgebouwd rond de 3.x-types. Na de ArchiMate-4-merge staat
 * er in de data `archimate.Process`, wat hier `undefined` gaf: geen vorm, geen
 * laagkleur, elk gemigreerd concept viel terug op een grijze rechthoek.
 * `legacyTypeIdFor` vertaalt terug via het compat-profiel — en dat profiel is
 * precies wat bepaalt of het een business- of applicatie-kleur hoort te zijn.
 */
function typeInfoForConcept(
  typeId: string,
  stereotypes: readonly string[] = [],
): { shape: NodeShape; layer: LayerSlug } | undefined {
  return TYPE_INFO[legacyTypeIdFor(typeId, stereotypes)] ?? TYPE_INFO[typeId];
}

type NodeShape = "rect" | "round" | "rhombus";
type LayerSlug =
  | "business"
  | "application"
  | "technology"
  | "motivation"
  | "strategy"
  | "implementation"
  | "composite";

const LAYER_STYLES: Record<LayerSlug, { fill: string; stroke: string }> = {
  business: { fill: "#FFF6B0", stroke: "#E5C500" },
  application: { fill: "#C7DFFC", stroke: "#5B8DEF" },
  technology: { fill: "#C5E8C5", stroke: "#4FAA5E" },
  motivation: { fill: "#DCC8E5", stroke: "#9F70B5" },
  strategy: { fill: "#FCDBB7", stroke: "#E69A4D" },
  implementation: { fill: "#FCEBC7", stroke: "#D9B66B" },
  composite: { fill: "#E2E8F0", stroke: "#94A3B8" },
};

/** Types whose ArchiMate convention is `source = part, target = whole`. The
 *  Mermaid arrow direction stays source → target same as our domain; the
 *  relation type tag tells the reader which end is which. */
const COMPOSITION_LIKE = new Set(["archimate.Composition", "archimate.Aggregation"]);

export interface MermaidEmitOptions {
  /** Preferred display language for labels. Falls back to `nl`, then any. */
  lang?: string;
  /** Layout direction. Defaults to `LR` (left → right). */
  direction?: "LR" | "TB" | "RL" | "BT";
}

export function emitArchiMateMermaid(
  concepts: readonly Concept[],
  relations: readonly Relation[],
  options: MermaidEmitOptions = {},
): string {
  const lang = options.lang ?? "nl";
  const direction = options.direction ?? "LR";
  const lines: string[] = [];
  lines.push(`flowchart ${direction}`);

  // classDef declarations — only emit those that are actually used.
  const usedLayers = new Set<LayerSlug>();

  const visibleConcepts = concepts.filter((c) => !c.tombstoneInBranch);
  // Skip dangling relations — i.e. relations whose source or target
  // points to a concept that isn't in `visibleConcepts`. If we emit
  // them, Mermaid silently fabricates a grey default-style node with
  // the alias as label, which clutters the diagram and confuses the
  // reader. The canvas drops them too, for the same reason.
  const visibleConceptIds = new Set(visibleConcepts.map((c) => c.id));
  const visibleRelations = relations.filter(
    (r) =>
      !r.tombstoneInBranch &&
      visibleConceptIds.has(r.sourceConceptId) &&
      visibleConceptIds.has(r.targetConceptId),
  );

  for (const c of visibleConcepts) {
    const info = typeInfoForConcept(c.typeId, c.stereotypes);
    if (info) usedLayers.add(info.layer);
  }

  if (usedLayers.size > 0) lines.push("");
  for (const layer of usedLayers) {
    const style = LAYER_STYLES[layer];
    lines.push(
      `  classDef ${layer} fill:${style.fill},stroke:${style.stroke},stroke-width:2px,color:#0f172a`,
    );
  }

  // Make every edge visually noticeable. Mermaid's default stroke is
  // ~1px in a soft grey, which on a busy diagram all but disappears.
  // Bump width and use a darker slate so arrows read as cleanly as the
  // node borders do.
  if (visibleRelations.length > 0) {
    lines.push("  linkStyle default stroke:#334155,stroke-width:2px,fill:none");
  }

  // Concept nodes. Mermaid only allows `%%` comments on a line of their
  // own — inline comments after a node-spec are parsed as part of the
  // node and corrupt the diagram. So we put the typeId tag on a separate
  // line preceding the node. The parser picks it up from the preceding
  // comment.
  if (visibleConcepts.length > 0) lines.push("");
  for (const c of visibleConcepts) {
    const info = typeInfoForConcept(c.typeId, c.stereotypes);
    const shape = info?.shape ?? "rect";
    const layerCls = info ? `:::${info.layer}` : "";
    const id = sanitizeId(c.id);
    const label = escapeLabel(resolveBilingual(c.name, lang) || c.id);
    const open = shape === "round" ? '("' : shape === "rhombus" ? '{"' : '["';
    const close = shape === "round" ? '")' : shape === "rhombus" ? '"}' : '"]';
    lines.push(`  %% type:${c.typeId}`);
    lines.push(`  ${id}${open}${label}${close}${layerCls}`);
  }

  // Relations — always `-->`, with the user-supplied label between
  // pipes (or no pipes if there is no label). The relation type goes on
  // a preceding comment line, same as for nodes. Realization uses the
  // dotted `-.->` arrow as a visual cue; the comment remains the source
  // of truth on parse.
  if (visibleRelations.length > 0) lines.push("");
  for (const r of visibleRelations) {
    const fromId = sanitizeId(r.sourceConceptId);
    const toId = sanitizeId(r.targetConceptId);
    const userLabel = escapeLabel(resolveBilingual(r.label, lang));
    const arrow = r.typeId === "archimate.Realization" ? "-.->" : "-->";
    lines.push(`  %% type:${r.typeId}`);
    if (COMPOSITION_LIKE.has(r.typeId)) {
      lines.push("  %% source=part target=whole");
    }
    if (userLabel) {
      lines.push(`  ${fromId} ${arrow}|"${userLabel}"| ${toId}`);
    } else {
      lines.push(`  ${fromId} ${arrow} ${toId}`);
    }
  }

  return `${lines.join("\n")}\n`;
}

// ---- Helpers --------------------------------------------------------------

/** Mermaid identifiers must be alphanumeric + underscore. UUIDs contain
 *  hyphens, so prefix with `n_` and replace hyphens with underscores —
 *  same convention as the PlantUML emitter. */
function sanitizeId(id: string): string {
  return `n_${id.replace(/-/g, "_")}`;
}

/** Mermaid label-quoting: the only thing that breaks `|"…"|` is a double
 *  quote inside the label. We escape it with `&quot;` so Mermaid renders
 *  it correctly. */
function escapeLabel(s: string): string {
  if (!s) return "";
  return s.replace(/"/g, "&quot;");
}

function resolveBilingual(t: BilingualText | undefined, lang: string): string {
  if (!t) return "";
  if (t[lang]) return t[lang]!;
  // Fallback chain: nl → en → first available.
  if (t.nl) return t.nl;
  if (t.en) return t.en;
  const first = Object.values(t).find((v) => typeof v === "string" && v.length > 0);
  return first ?? "";
}
